import { useState, useRef } from "react";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Award, Download, Share2, CheckCircle, Leaf, Star,
  Calendar, School, User, Trophy, Printer
} from "lucide-react";

// Sanitize text for safe insertion into HTML
const sanitizeText = (text) => DOMPurify.sanitize(text || '', { ALLOWED_TAGS: [] });

const CertificateView = ({ certificate, onClose }) => {
  const certificateRef = useRef(null);
  
  const handlePrint = () => {
    const printContent = certificateRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>ROOTSPACE Certificate - ${sanitizeText(certificate.user_name)}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Inter', sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #f5f5f5;
              padding: 20px;
            }
            .certificate {
              width: 800px;
              background: white;
              border: 8px solid ${certificate.level_color};
              border-radius: 16px;
              padding: 48px;
              position: relative;
              overflow: hidden;
            }
            .certificate::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 120px;
              background: linear-gradient(135deg, ${certificate.level_color}22, ${certificate.level_color}11);
            }
            .header {
              text-align: center;
              position: relative;
              z-index: 1;
              margin-bottom: 32px;
            }
            .logo {
              font-family: 'Playfair Display', serif;
              font-size: 32px;
              font-weight: 700;
              color: ${certificate.level_color};
              margin-bottom: 8px;
            }
            .title {
              font-family: 'Playfair Display', serif;
              font-size: 28px;
              color: #1f2937;
              margin-bottom: 4px;
            }
            .subtitle {
              font-size: 14px;
              color: #6b7280;
            }
            .recipient {
              text-align: center;
              margin: 32px 0;
            }
            .recipient-label {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 8px;
            }
            .recipient-name {
              font-family: 'Playfair Display', serif;
              font-size: 36px;
              font-weight: 600;
              color: #1f2937;
            }
            .achievement {
              text-align: center;
              margin: 24px 0;
              padding: 24px;
              background: ${certificate.level_color}11;
              border-radius: 12px;
            }
            .level-badge {
              display: inline-block;
              padding: 8px 24px;
              background: ${certificate.level_color};
              color: white;
              border-radius: 24px;
              font-weight: 600;
              font-size: 18px;
              margin-bottom: 8px;
            }
            .phase {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 8px;
            }
            .description {
              font-size: 13px;
              color: #4b5563;
            }
            .challenge-info {
              text-align: center;
              margin: 24px 0;
            }
            .challenge-title {
              font-size: 18px;
              font-weight: 600;
              color: #1f2937;
              margin-bottom: 4px;
            }
            .challenge-detail {
              font-size: 13px;
              color: #6b7280;
            }
            .standards {
              margin: 24px 0;
              padding: 16px;
              background: #f9fafb;
              border-radius: 8px;
            }
            .standards-title {
              font-size: 12px;
              font-weight: 600;
              color: #6b7280;
              margin-bottom: 8px;
              text-transform: uppercase;
            }
            .standards-list {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
            }
            .standard-badge {
              padding: 4px 12px;
              background: ${certificate.level_color}22;
              color: ${certificate.level_color};
              border-radius: 12px;
              font-size: 12px;
              font-weight: 500;
            }
            .footer {
              display: flex;
              justify-content: space-between;
              margin-top: 32px;
              padding-top: 24px;
              border-top: 2px solid #e5e7eb;
            }
            .footer-item {
              text-align: center;
            }
            .footer-label {
              font-size: 11px;
              color: #9ca3af;
              margin-bottom: 4px;
              text-transform: uppercase;
            }
            .footer-value {
              font-size: 13px;
              color: #4b5563;
              font-weight: 500;
            }
            .cert-number {
              text-align: center;
              margin-top: 16px;
              font-size: 11px;
              color: #9ca3af;
            }
            @media print {
              body { background: white; }
              .certificate { border: 8px solid ${certificate.level_color}; }
            }
          </style>
        </head>
        <body>
          <div class="certificate">
            <div class="header">
              <div class="logo">🌱 ROOTSPACE</div>
              <div class="title">Certificate of Achievement</div>
              <div class="subtitle">Garden Design & Agricultural Sciences</div>
            </div>
            
            <div class="recipient">
              <div class="recipient-label">This is to certify that</div>
              <div class="recipient-name">${certificate.user_name}</div>
            </div>
            
            <div class="achievement">
              <div class="level-badge">${certificate.level_title}</div>
              <div class="phase">${certificate.level_phase}</div>
              <div class="description">${certificate.level_description}</div>
            </div>
            
            <div class="challenge-info">
              <div class="challenge-title">${certificate.challenge_title}</div>
              ${certificate.design_name ? `<div class="challenge-detail">Design: ${certificate.design_name}</div>` : ''}
              ${certificate.yield_achieved ? `<div class="challenge-detail">Estimated Yield: ${certificate.yield_achieved} lbs</div>` : ''}
              ${certificate.rank ? `<div class="challenge-detail">Placed #${certificate.rank} in competition</div>` : ''}
            </div>
            
            ${certificate.standards_demonstrated?.length > 0 ? `
              <div class="standards">
                <div class="standards-title">Standards Demonstrated</div>
                <div class="standards-list">
                  ${certificate.standards_demonstrated.map(std => `<span class="standard-badge">${std}</span>`).join('')}
                </div>
              </div>
            ` : ''}
            
            <div class="footer">
              <div class="footer-item">
                <div class="footer-label">Date Issued</div>
                <div class="footer-value">${new Date(certificate.issued_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>
              ${certificate.teacher_name ? `
                <div class="footer-item">
                  <div class="footer-label">Instructor</div>
                  <div class="footer-value">${certificate.teacher_name}</div>
                </div>
              ` : ''}
              ${certificate.school_name ? `
                <div class="footer-item">
                  <div class="footer-label">Institution</div>
                  <div class="footer-value">${certificate.school_name}</div>
                </div>
              ` : ''}
            </div>
            
            <div class="cert-number">
              Certificate #${certificate.certificate_number}<br/>
              Verify at rootspace.pro/verify/${certificate.certificate_number}
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        ref={certificateRef}
      >
        {/* Certificate Preview */}
        <div 
          className="p-8 relative overflow-hidden"
          style={{ borderTop: `8px solid ${certificate.level_color}` }}
        >
          {/* Background Pattern */}
          <div 
            className="absolute top-0 left-0 right-0 h-32 opacity-10"
            style={{ background: `linear-gradient(135deg, ${certificate.level_color}, transparent)` }}
          />
          
          {/* Header */}
          <div className="text-center relative z-10 mb-8">
            <div className="text-3xl font-bold mb-2" style={{ color: certificate.level_color }}>
              🌱 ROOTSPACE
            </div>
            <h2 className="text-2xl font-serif font-semibold">Certificate of Achievement</h2>
            <p className="text-sm text-muted-foreground">Garden Design & Agricultural Sciences</p>
          </div>
          
          {/* Recipient */}
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground mb-2">This is to certify that</p>
            <h3 className="text-3xl font-serif font-semibold">{certificate.user_name}</h3>
          </div>
          
          {/* Achievement Level */}
          <div 
            className="text-center p-6 rounded-xl mb-6"
            style={{ backgroundColor: `${certificate.level_color}11` }}
          >
            <Badge 
              className="text-lg px-6 py-2 mb-2"
              style={{ backgroundColor: certificate.level_color }}
            >
              {certificate.level_title}
            </Badge>
            <p className="text-sm text-muted-foreground mb-1">{certificate.level_phase}</p>
            <p className="text-sm">{certificate.level_description}</p>
          </div>
          
          {/* Challenge Info */}
          <div className="text-center mb-6">
            <h4 className="font-semibold text-lg">{certificate.challenge_title}</h4>
            {certificate.design_name && (
              <p className="text-sm text-muted-foreground">Design: {certificate.design_name}</p>
            )}
            {certificate.yield_achieved > 0 && (
              <p className="text-sm text-muted-foreground">Estimated Yield: {certificate.yield_achieved} lbs</p>
            )}
            {certificate.rank && (
              <Badge variant="outline" className="mt-2">
                <Trophy className="w-3 h-3 mr-1" /> Placed #{certificate.rank}
              </Badge>
            )}
          </div>
          
          {/* Standards */}
          {certificate.standards_demonstrated?.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                Standards Demonstrated
              </p>
              <div className="flex flex-wrap gap-2">
                {certificate.standards_demonstrated.map((std, i) => (
                  <Badge 
                    key={i} 
                    variant="outline"
                    style={{ borderColor: certificate.level_color, color: certificate.level_color }}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" /> {std}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Footer */}
          <div className="flex justify-between items-center pt-6 border-t">
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase">Date Issued</p>
              <p className="text-sm font-medium">
                {new Date(certificate.issued_date).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })}
              </p>
            </div>
            {certificate.teacher_name && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase">Instructor</p>
                <p className="text-sm font-medium">{certificate.teacher_name}</p>
              </div>
            )}
            {certificate.school_name && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase">Institution</p>
                <p className="text-sm font-medium">{certificate.school_name}</p>
              </div>
            )}
          </div>
          
          {/* Certificate Number */}
          <div className="text-center mt-4">
            <p className="text-xs text-muted-foreground">
              Certificate #{certificate.certificate_number}
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="p-4 bg-gray-50 rounded-b-2xl flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handlePrint} data-testid="print-certificate-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Certificate
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Certificate Progress Card Component
export const CertificateProgressCard = ({ progress, onViewCertificates }) => {
  if (!progress) return null;
  
  const { current_level, next_level, challenges_completed } = progress;
  const progressPercent = next_level 
    ? ((challenges_completed - (current_level?.min_challenges || 0)) / 
       (next_level.min_challenges - (current_level?.min_challenges || 0))) * 100
    : 100;

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Award className="w-5 h-5 text-green-600" />
          Certificate Progress
        </h3>
        {onViewCertificates && (
          <Button variant="outline" size="sm" onClick={onViewCertificates}>
            View Certificates
          </Button>
        )}
      </div>
      
      <div className="flex items-center gap-4 mb-4">
        <div 
          className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
          style={{ backgroundColor: current_level?.color || '#22c55e' }}
        >
          <Leaf className="w-8 h-8" />
        </div>
        <div>
          <p className="font-bold text-lg">{current_level?.title || 'Green Hand'}</p>
          <p className="text-sm text-muted-foreground">{current_level?.phase || 'Germination Phase'}</p>
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>{challenges_completed} challenges completed</span>
          {next_level && <span>Next: {next_level.title} ({next_level.min_challenges})</span>}
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${Math.min(progressPercent, 100)}%`,
              backgroundColor: current_level?.color || '#22c55e'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CertificateView;
